/**
 * Old-versions review modal — WHAT "Clear old versions" would delete.
 *
 * The reclaimable line used to offer a destructive action over a number
 * the user couldn't inspect ("65 items" of… what?). This modal answers
 * it: every save keeps the file's previous content as a version; these
 * are the superseded ones, grouped per file (biggest total first) with
 * each version's date, size and origin. The feed is the same §2.8
 * exclusion the line counts and the purge deletes, so what you see is
 * exactly what Clear removes — no more, no less.
 *
 * Versions are individually selectable (checkbox per version, group
 * checkbox per file — same grammar as the duplicates modal); the footer
 * deletes the selection through the id-filtered purge, whose non-current
 * guard is server-side. The page row keeps the all-at-once Clear. Gate
 * mirrors the drill-down: owner/admin (file names cross
 * folder-permission boundaries); a plain member's 403 renders as copy.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { useTheme, getThemeClass, useConfirm } from '@papan/core-ui/ui'
import { storageApi, type VersionsResponse } from '../../client/storage'
import { formatBytes } from '../budget/bits'
import { Checkbox, shortenPath, fmtDate, folderLabel } from './shared'

export function VersionsModal({
  tenantId,
  onClose,
  onChanged,
  onOpenFile,
  canPurge = true,
}: {
  tenantId: string
  onClose: () => void
  /** #1102 item 4: `manage_file_versions:write` — a reader (read only) sees the list, the delete stays disabled. */
  canPurge?: boolean
  /** Fired after a successful purge so the page refreshes its totals. */
  onChanged: () => void
  /** Shell-owned navigate-and-reveal (followups §2.4). */
  onOpenFile?: (folderId: string | null, fileId: string) => void
}) {
  const { t } = useTranslation('core-ui')
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const confirm = useConfirm()

  const [data, setData] = useState<VersionsResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [forbidden, setForbidden] = useState(false)
  const [loading, setLoading] = useState(true)
  const [deleting, setDeleting] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [expanded, setExpanded] = useState<Set<string> | null>(null)

  const loadSeq = useRef(0)
  const load = useCallback(() => {
    const seq = ++loadSeq.current
    setLoading(true)
    storageApi.listVersions(tenantId)
      .then((d) => {
        if (seq !== loadSeq.current) return
        setData(d)
        setError(null)
        setForbidden(false)
      })
      .catch((e: any) => {
        if (seq !== loadSeq.current) return
        if (e?.status === 403) setForbidden(true)
        else setError(e?.message || 'Failed to load versions')
      })
      .finally(() => { if (seq === loadSeq.current) setLoading(false) })
  }, [tenantId])

  useEffect(() => {
    load()
    return () => { loadSeq.current++ }
  }, [load])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const groups = data?.files ?? []
  const expandedSet = expanded ?? new Set(groups.length ? [groups[0].fileId] : [])
  const toggleExpanded = (fileId: string) => {
    const next = new Set(expandedSet)
    if (next.has(fileId)) next.delete(fileId)
    else next.add(fileId)
    setExpanded(next)
  }

  const toggleVersion = (id: string) => {
    setSelected((s) => {
      const next = new Set(s)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const allVersions = groups.flatMap((g) => g.versions)
  const selectedVersions = allVersions.filter((v) => selected.has(v.id))
  const selectedBytes = selectedVersions.reduce((s, v) => s + v.size, 0)

  const handleDeleteSelected = async () => {
    if (!selectedVersions.length) return
    const ok = await confirm({
      title: t('settings.storage.manage.deleteSelected'),
      message: t('settings.storage.versions.deleteSelectedConfirm', {
        count: selectedVersions.length,
        size: formatBytes(selectedBytes),
      }),
      type: 'danger',
      confirmText: t('common:delete'),
    })
    if (!ok) return

    setDeleting(true)
    try {
      await storageApi.purgeVersions(tenantId, selectedVersions.map((v) => v.id))
      setSelected(new Set())
    } catch (e: any) {
      setError(e?.message || 'Failed to delete versions')
    } finally {
      setDeleting(false)
      // Reload REGARDLESS of outcome. A selection over 500 is sent as
      // several requests, so a failure can arrive with earlier batches
      // already committed — reloading only on success would leave the
      // list showing versions that are already gone, and the headline
      // figure disagreeing with the drive (review 2026-08-06). Groups
      // change shape anyway (a fully-cleared file drops out), so this is
      // a reload rather than a splice.
      onChanged()
      load()
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 ws-anim-fade-in" onClick={onClose} />
      <div className={`relative z-10 max-w-lg w-full max-h-[80vh] flex flex-col ${themeClass} ws-glass-modal ws-anim-modal-in`}>
        <div className="flex items-baseline justify-between gap-3 px-5 pt-4 pb-3">
          <div className="min-w-0">
            <h3 className="text-base font-semibold themed-text truncate">{t('settings.storage.reclaim.versions')}</h3>
            {/* The answer to "what IS an old version?" — the explainer the
                bare row action was missing. */}
            <p className="themed-text-muted text-xs mt-0.5">{t('settings.storage.versions.desc')}</p>
          </div>
          <button
            onClick={onClose}
            aria-label={t('common:close')}
            className="p-1 -m-1 themed-text-muted hover:themed-text transition-colors rounded"
          >
            ✕
          </button>
        </div>

        <div className="px-5 overflow-y-auto min-h-0 flex-1">
          {loading && <div className="py-6 text-sm themed-text-muted">{t('common:loading')}</div>}
          {forbidden && (
            <div className="py-6 text-sm themed-text-muted">{t('settings.storage.manage.noAccess')}</div>
          )}
          {error && <div className="py-2 text-sm text-red-400">{error}</div>}
          {data && !loading && groups.length === 0 && !forbidden && (
            <div className="py-6 text-sm themed-text-muted">{t('settings.storage.versions.none')}</div>
          )}

          {groups.map((g) => {
            const isOpen = expandedSet.has(g.fileId)
            const dir = folderLabel(g.folder)
            const fullPath = dir === '/' ? `/${g.name}` : `${dir}/${g.name}`
            const allSelected = g.versions.every((v) => selected.has(v.id))
            const someSelected = !allSelected && g.versions.some((v) => selected.has(v.id))
            return (
              <div key={g.fileId} className="border-b themed-border-secondary last:border-b-0">
                <div className="flex items-center gap-2.5 py-2 text-sm">
                  {/* Every listed version is already non-current, so unlike
                      the duplicates modal there is no copy that must be
                      spared — the group tick simply selects the whole file's
                      history. */}
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={() => {
                      setSelected((prev) => {
                        const next = new Set(prev)
                        const anyIn = g.versions.some((v) => next.has(v.id))
                        if (anyIn) for (const v of g.versions) next.delete(v.id)
                        else for (const v of g.versions) next.add(v.id)
                        return next
                      })
                    }}
                    aria-label={g.name}
                  />
                  <button
                    onClick={() => toggleExpanded(g.fileId)}
                    className="flex items-center gap-1.5 flex-1 min-w-0 text-left group"
                    aria-expanded={isOpen}
                  >
                    <span className="themed-text-muted text-[10px] w-3 shrink-0">{isOpen ? '▾' : '▸'}</span>
                    <span className="themed-text font-medium truncate group-hover:underline underline-offset-2" title={fullPath}>
                      {shortenPath(fullPath, 40)}
                    </span>
                  </button>
                  <span className="themed-text-muted text-xs tabular-nums shrink-0">
                    {t('settings.storage.versions.fileInfo', { count: g.items })}
                  </span>
                  <span className="themed-text tabular-nums text-xs shrink-0">{formatBytes(g.totalBytes)}</span>
                  {onOpenFile && (
                    <button
                      onClick={() => onOpenFile(g.folderId, g.fileId)}
                      className="themed-text-muted hover:themed-text text-xs shrink-0 px-0.5"
                      title={t('settings.storage.manage.openInDrive')}
                      aria-label={t('settings.storage.manage.openInDrive')}
                    >
                      ↗
                    </button>
                  )}
                </div>
                {isOpen && g.versions.map((v) => (
                  <label key={v.id} className="flex items-center gap-2.5 py-1.5 pl-6 pr-1 text-sm cursor-pointer hover:themed-bg-tertiary rounded">
                    <Checkbox checked={selected.has(v.id)} onChange={() => toggleVersion(v.id)} />
                    {/* Origin of the version — the human answer to "where did
                        this come from": an edit, an upload, an external sync. */}
                    <span className="themed-text-muted flex-1 min-w-0 truncate text-xs" title={v.description ?? v.source ?? undefined}>
                      {v.description || v.source || '—'}
                    </span>
                    <span className="themed-text-muted text-xs tabular-nums w-14 text-right shrink-0">{fmtDate(v.createdAt)}</span>
                    <span className="themed-text tabular-nums text-xs w-16 text-right shrink-0">{formatBytes(v.size)}</span>
                  </label>
                ))}
              </div>
            )
          })}

          {/* Past the 100-file cap the totals stop matching the visible
              groups and a selection can't reach the rest — say so instead of
              letting the numbers disagree in silence. Never renders for a
              normal tenant. */}
          {data?.truncated && (
            <div className="py-2 themed-text-muted text-xs">
              {t('settings.storage.versions.truncated', {
                count: data.totalItems - groups.reduce((s, g) => s + g.items, 0),
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-5 py-3 border-t themed-border-secondary">
          <span className="themed-text-muted text-xs tabular-nums">
            {t('settings.storage.manage.selectedSummary', {
              count: selected.size,
              size: formatBytes(selectedBytes),
            })}
          </span>
          <button
            onClick={handleDeleteSelected}
            disabled={!canPurge || !selected.size || deleting}
            className="text-sm px-3 py-1.5 rounded-md bg-red-500/90 hover:bg-red-500 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {deleting ? t('common:loading') : t('settings.storage.manage.deleteSelected')}
          </button>
        </div>
      </div>
    </div>
  )
}
